import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import { Button, DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NISpdf from "@tCentro/Icons/NISpdf";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import chunk from "lodash/chunk";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface NISProductDetailsAttributesDocumentsStyles {
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    specAttrdataTableCellCss?: InjectableCss;
    specDataTableCellCss?: InjectableCss;
    dataRowCss?: InjectableCss;
    attributesTitle?: TypographyPresentationProps;
    additionalLink?: TypographyPresentationProps;
    accordionContainer?: InjectableCss;
    feedbackWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    pdfIconStyles?: IconPresentationProps;
    mainWrapper?: InjectableCss;
    gridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    productButton?: InjectableCss;
}

export const specificationsStyles: NISProductDetailsAttributesDocumentsStyles = {
    documentGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            border-bottom: 1px solid #e0dfe0;
            width: 100%;
            padding: 15px 20px;
            margin-left: auto !important;
            margin-right: auto !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 93% !important;
                        padding: 15px 0px;
                    `,
                    css`
                        max-width: 93% !important;
                        padding: 15px 0px;
                    `,
                    css`
                        max-width: 96% !important;
                    `,
                    css`
                        max-width: 96% !important;
                    `,
                    css`
                        max-width: 98% !important;
                    `,
                ])}
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            margin-left: 5px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: 24px;
        `,
    },
    attributeTypeLabelText: {
        css: css`
            font-weight: 600;
            letter-spacing: 0;
            text-align: justify;
            color: #333333;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        padding: 0;
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                ])}
        `,
    },

    attributeTypeValuesText: {
        css: css`
            color: #333333;
            font-weight: 400;
            letter-spacing: 0;
            text-align: justify;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.base};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.base};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.large};
                        line-height: 28px;
                        > ul {
                            padding-left: 20px;
                        }
                    `,
                ])}
        `,
    },

    attributesTitle: {
        css: css`
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            font-weight: 600;
            font-size: ${NISCO.TYPOGRAPHY.size.xxlarge};
            line-height: 40px;
            color: ${NISCO.COLORS.primaryColor};
            margin: 0px;
            display: flex;
            gap: 10px;
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        font-size: 24px;
                        line-height: 32px;
                        margin-bottom: 16px;
                    `,
                    css`
                        flex-direction: column;
                        font-size: 24px;
                        line-height: 32px;
                        margin-bottom: 16px;
                    `,
                    css`
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    additionalLink: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            letter-spacing: 0%;
            vertical-align: middle;
            color: #0f96d9;
            cursor: pointer;
            display: flex;
            align-items: center;
        `,
    },
    specAttrdataTableCellCss: {
        css: css`
            padding: 0 16px;
            white-space: normal;
            box-sizing: content-box;
            width: 50%;
            height: auto;
            max-width: 300px;
            border: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    css`
                        width: 70%;
                    `,
                    null,
                ])}
        `,
    },

    specDataTableCellCss: {
        css: css`
            white-space: normal;
            border: none;
            height: auto;
            justify-content: flex-start;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dataRowCss: {
        css: css`
            display: grid;
            gap: 24px;
            align-items: center;
            background: transparent;
            grid-template-columns: 1fr;
            & > div {
                width: 50%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                background: transparent;
                width: 100%;
                padding: 16px;
                height: 100%;
                & > td {
                    display: flex;
                    align-items: center;
                }
            }
            :nth-child(odd) {
                & > div {
                    background-color: #fff !important;
                    border-bottom: 1px solid #e0dfe0;
                }
            }
            :nth-child(even) {
                background-color: transparent !important;
                & > div {
                    background-color: #f5f6f7 !important;
                    border-bottom: 1px solid #e0dfe0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    documentLink: {
        css: css`
            > span {
                display: flex;
                align-items: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > span {
                            align-items: flex-start;
                        }
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                    `,
                    css`
                        > span {
                            align-items: flex-start;
                        }
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                    `,
                    css`
                        > span {
                            align-items: flex-start;
                        }
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    accordionContainer: {
        css: css`
            // margin: 15px 0px;
            width: 100%;
            display: block;
            @media (max-width: 767px) {
                margin: 24px 0px;
            }
        `,
    },
    feedbackWrapper: {
        css: css`
            margin: 20px 0px;
            display: flex;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 24px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        gap: 16px;
                        // margin-bottom: 30px;
                        width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        gap: 16px;
                        // margin-bottom: 30px;
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    pdfIconStyles: {
        css: css`
            position: relative;
            margin-right: 8px;
            width: 20px;
            height: 20px;
            svg {
                width: 20px;
                height: 20px;
            }
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    mainWrapper: {
        css: css`
            margin-bottom: 20px;
            margin-top: 54px;
            margin-left: 19px;
            padding-right: 41px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 30px;
                        div[class^="GridItemStyle"] {
                            margin-left: 0px !important;
                            margin-right: 0px !important;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        }
                    `,
                    css`
                        margin-top: 30px;
                        div[class^="GridItemStyle"] {
                            margin-left: 0px !important;
                            margin-right: 0px !important;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        }
                    `,
                    css`
                        margin-top: -5px;
                        margin-left: -16px;
                        padding-right: 0px;
                        div[class^="GridItemStyle"] {
                            margin-left: 0px !important;
                            margin-right: 0px !important;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },

    gridItem: {
        css: css`
            align-items: flex-start;
            justify-content: center;
            flex-direction: column;
            gap: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: 0px;
                    `,
                    css`
                        gap: 0px;
                    `,
                    css`
                        gap: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    headingText: {
        css: css`
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            font-weight: 600;
            font-size: 40px;
            line-height: 48px;
            color: ${NISCO.COLORS.primaryColor};
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.xxlarge};
                        line-height: 40px;
                        text-align: left;
                        // margin-bottom: 16px;
                        margin-right: auto;
                    `,
                    css`
                        font-size: ${NISCO.TYPOGRAPHY.size.xxlarge};
                        line-height: 40px;
                        text-align: left;
                        // margin-bottom: 16px;
                        margin-right: auto;
                    `,
                    css`
                        font-size: 32px;
                        line-height: 40px;
                        text-align: left;
                        margin-bottom: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    productButton: {
        css: css`
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            font-weight: 700;
            font-size: ${NISCO.TYPOGRAPHY.size.base};
            line-height: 16px;
            text-align: center;
            vertical-align: middle;
            text-transform: uppercase;
            cursor: pointer;
            display: flex;
            align-items: center;
            max-height: 40px;
            border-radius: 4px !important;
            span {
                font-weight: 700;
                font-size: ${NISCO.TYPOGRAPHY.size.base};
                line-height: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: auto;
                        width: 100%;
                        justify-content: center;
                    `,
                    css`
                        margin-right: auto;
                        width: 100%;
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    documentsContainer: {
        css: css`
            margin-top: 30px;
            margin-bottom: 25px;
            display: block;
        `,
    },
};

const styles = specificationsStyles;

const NISProductDetailsAttributesDocuments: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const allAttributTypesObject = chunk(attributeTypes, 1);
    const [allAttributTypes, setAllAttributTypes] = React.useState(allAttributTypesObject);
    const renderingDocuments = showDocuments && documents && documents.length > 0;

    const renderingAttributes = showAttributes && attributeTypes && attributeTypes?.length > 0;
    const renderingSpecifications = specifications && specifications?.length > 0;

    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    const documentRef = React.useRef<HTMLDivElement | null>(null);

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const handleScroll = () => {
        if (documentRef.current) {
            const header = document.querySelector('[data-test-selector="header"]') as HTMLElement | null;
            const headerHeight = header ? header.offsetHeight : 0;

            const yOffset = -headerHeight - 20; // 20px extra padding
            const y = documentRef.current!.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const documentsAccordion = renderingDocuments && (
        <StyledWrapper {...styles.accordionContainer} ref={documentRef}>
            <Typography {...styles.attributesTitle} as="p">
                {translate("Documents ")}
            </Typography>
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(
                    document =>
                        document.documentType.toLowerCase() !== "article" && (
                            <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                <Link
                                    href={document.filePath}
                                    target="_new"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                    <Icon src={NISpdf} />
                                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                                </Link>
                            </GridItem>
                        ),
                )}
            </GridContainer>
        </StyledWrapper>
    );

    const attributesSection = renderingAttributes && attributeTypes?.length !== 0 && (
        <StyledWrapper {...styles.accordionContainer}>
            <Typography {...styles.attributesTitle} as="p">
                {translate("Attributes")}
            </Typography>
            <DataTable>
                <DataTableBody>
                    {allAttributTypes?.map(attributeType => {
                        return (
                            <>
                                <DataTableRow {...styles.dataRowCss}>
                                    {attributeType?.map(
                                        (specification: {
                                            name: string | number | undefined;
                                            label: any;
                                            attributeValues: any;
                                        }) => (
                                            // eslint-disable-next-line react/jsx-key
                                            <StyledWrapper>
                                                <DataTableCell {...styles.specAttrdataTableCellCss}>
                                                    <Typography {...styles.attributeTypeLabelText}>
                                                        {specification.label}
                                                    </Typography>
                                                </DataTableCell>

                                                <DataTableCell {...styles.specDataTableCellCss}>
                                                    <Typography {...styles.attributeTypeValuesText}>
                                                        {specification.attributeValues
                                                            ?.map(
                                                                (attributeValue: { valueDisplay: any }) =>
                                                                    attributeValue.valueDisplay,
                                                            )
                                                            .join(", ")}
                                                    </Typography>
                                                </DataTableCell>
                                            </StyledWrapper>
                                        ),
                                    )}
                                </DataTableRow>
                            </>
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
    return (
        <GridContainer {...styles.mainWrapper} data-test-selector="productDetails_attributes">
            <GridItem {...styles.gridItem} width={12}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.headingText}>{translate("Product Details")}</Typography>

                    {renderingDocuments && renderingAttributes && (
                        <Button
                            variant="secondary"
                            {...styles.productButton}
                            onClick={handleScroll}
                            data-test-selector="productDetails_specifications_viewDetails"
                        >
                            <Icon src={NISpdf} {...styles.pdfIconStyles} />
                            <Typography>{translate("View Product Spec Sheet")}</Typography>
                        </Button>
                    )}
                </StyledWrapper>

                {attributesFirst && attributesSection}
                {documentsFirst && documentsAccordion}

                {!attributesFirst && attributesSection}
                {!documentsFirst && documentsAccordion}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(NISProductDetailsAttributesDocuments),
    definition: {
        displayName: "NIS Product Details Attributes & Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
